import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { ErrorNotice, useAction } from "../components/ui";
import { api, type Pool } from "../lib/api";
import { whenLast } from "../lib/dates";
import { people } from "./AddToPool";

const pad = (n: number) => String(n).padStart(2, "0");

/** Talent pools: the agency's saved lists of candidates to come back to. */
export function Pools() {
  const navigate = useNavigate();
  const [pools, setPools] = useState<Pool[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const load = useAction();
  const save = useAction();

  useEffect(() => {
    void load.run(async () => setPools((await api.pools()).pools));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save.run(async () => navigate(`/pools/${(await api.createPool(name.trim(), description.trim())).pool.id}`));
  };

  return (
    <>
      <div className="page-head">
        <p className="label">Talent pools</p>
        <h1 className="display">
          People to <em>come back</em> to.
        </h1>
        <p className="lede">
          Keep lists of candidates by skill, place or what they're waiting for, then put a whole pool on a job in one go. Shared with your team.
        </p>
      </div>

      <section className="section" aria-label="Pools">
        <div className="section-head">
          <span className="label figures">{pools ? `${pad(pools.length)} ${pools.length === 1 ? "pool" : "pools"}` : "…"}</span>
          {!adding && (
            <button type="button" className="link-btn" onClick={() => setAdding(true)}>
              + New pool
            </button>
          )}
        </div>

        {adding && (
          <form className="form panel" onSubmit={submit} aria-label="New pool">
            <label className="field">
              <span className="label">Name</span>
              <input value={name} maxLength={100} required autoFocus placeholder="e.g. Leeds Go engineers" onChange={(e) => setName(e.target.value)} />
            </label>
            <label className="field">
              <span className="label">What it's for (optional)</span>
              <input value={description} maxLength={1000} placeholder="e.g. Open to hybrid, 70-90k" onChange={(e) => setDescription(e.target.value)} />
            </label>
            <ErrorNotice error={save.error} />
            <div className="button-row">
              <button className="btn" disabled={save.pending || !name.trim()}>
                {save.pending ? "Creating…" : "Create pool"}
              </button>
              <button type="button" className="link-btn" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </form>
        )}

        <ErrorNotice error={load.error} />
        <ol className="index">
          {pools?.map((p, i) => (
            <li key={p.id}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <Link className="primary" to={`/pools/${p.id}`}>
                  {p.name}
                </Link>
                {p.description && <span className="secondary">{p.description}</span>}
                <span className="secondary">
                  {people(p.size)} · changed {whenLast(p.updatedAt)}
                </span>
              </span>
              <span className="actions" />
            </li>
          ))}
          {pools && pools.length === 0 && (
            <li>
              <span className="empty">No pools yet. Create one here, or search Candidates and add the results to a new pool.</span>
            </li>
          )}
          {!pools && !load.error && (
            <li>
              <span className="empty">Loading…</span>
            </li>
          )}
        </ol>
      </section>
    </>
  );
}
